import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

/**
 * Admin panel: match listing, search, and deletion.
 */
export default function AdminMatches({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Counter state
  const [counter, setCounter] = useState(null);
  const [counterLoading, setCounterLoading] = useState(true);
  const [nextId, setNextId] = useState('');
  const [counterSaving, setCounterSaving] = useState(false);
  const [counterResult, setCounterResult] = useState('');
  const [counterError, setCounterError] = useState('');

  // Renumber state
  const [renumberOld, setRenumberOld] = useState('');
  const [renumberNew, setRenumberNew] = useState('');
  const [renumberLoading, setRenumberLoading] = useState(false);
  const [renumberResult, setRenumberResult] = useState(null);
  const [renumberError, setRenumberError] = useState('');

  // Delete stats state
  const [deleteId, setDeleteId] = useState('');
  const [deleteDjango, setDeleteDjango] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // Import stats state
  const [importId, setImportId] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  // Process demos state
  const [demosLoading, setDemosLoading] = useState(false);
  const [demosResult, setDemosResult] = useState(null);

  // Reconstruct match state (rebuild teams/winner/server from MatchZy)
  const [recId, setRecId] = useState('');
  const [recForce, setRecForce] = useState(false);
  const [recServerId, setRecServerId] = useState('');  // optional manual override
  const [recLoading, setRecLoading] = useState(false);
  const [recResult, setRecResult] = useState(null);
  const [recError, setRecError] = useState('');
  const [serverOptions, setServerOptions] = useState([]);  // list of guild + public servers

  // Reprocess demo state (advanced stats + positions + highlights)
  const [reproId, setReproId] = useState('');
  const [reproSkipAdv, setReproSkipAdv] = useState(false);
  const [reproSkipPos, setReproSkipPos] = useState(false);
  const [reproSkipHl, setReproSkipHl] = useState(false);
  const [reproLoading, setReproLoading] = useState(false);
  const [reproResult, setReproResult] = useState(null);
  const [reproError, setReproError] = useState('');

  // Confirm dialogs
  const [confirmRenumber, setConfirmRenumber] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // General error
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const r = await fetch(`${API_URL}/api/matches?limit=20`, { headers: getAuthHeaders() });
        if (r.ok) {
          const data = await r.json();
          setMatches(Array.isArray(data) ? data : data.results || []);
        }
      } catch {} finally { setLoading(false); }
    };
    fetchMatches();
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!perms.isOwner || !perms.guildId) return;
    const fetchServers = async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/admin/servers?guild_or_public=${perms.guildId}`,
          { headers: getAuthHeaders() },
        );
        if (r.ok) {
          const data = await r.json();
          const list = Array.isArray(data) ? data : data.results || [];
          setServerOptions(list);
        }
      } catch {
        // silent
      }
    };
    fetchServers();
  }, [perms.isOwner, perms.guildId, getAuthHeaders]);

  useEffect(() => {
    const fetchCounter = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/matches/counter`, { headers: getAuthHeaders() });
        if (r.ok) setCounter(await r.json());
      } catch {} finally { setCounterLoading(false); }
    };
    if (perms.hasCommand('admin.reset-counter')) fetchCounter();
    else setCounterLoading(false);
  }, [getAuthHeaders, perms]);

  const handleSetCounter = async () => {
    setCounterSaving(true);
    setCounterError('');
    setCounterResult('');
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/counter`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ next_id: parseInt(nextId) }),
      });
      const data = await r.json();
      if (!r.ok) setCounterError(data.error || data.detail || t('admin.errorGeneric'));
      else { setCounterResult(t('admin.matchCounterUpdated')); setCounter(data); setNextId(''); }
    } catch { setCounterError(t('settings.networkError')); }
    finally { setCounterSaving(false); }
  };

  const handleRenumber = async () => {
    setRenumberLoading(true);
    setRenumberError('');
    setRenumberResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/renumber`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_id: parseInt(renumberOld), new_id: parseInt(renumberNew) }),
      });
      const data = await r.json();
      if (!r.ok) setRenumberError(data.error || data.detail || t('admin.errorGeneric'));
      else { setRenumberResult(data); setRenumberOld(''); setRenumberNew(''); }
    } catch { setRenumberError(t('settings.networkError')); }
    finally { setRenumberLoading(false); }
  };

  const handleDeleteStats = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    setDeleteResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/delete-stats`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: parseInt(deleteId), delete_django: deleteDjango }),
      });
      const data = await r.json();
      if (!r.ok) setDeleteError(data.error || data.detail || t('admin.errorGeneric'));
      else { setDeleteResult(data); setDeleteId(''); }
    } catch { setDeleteError(t('settings.networkError')); }
    finally { setDeleteLoading(false); }
  };

  const handleImportStats = async () => {
    setImportLoading(true);
    setImportError('');
    setImportResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/import-stats`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: parseInt(importId) }),
      });
      const data = await r.json();
      if (!r.ok) setImportError(data.error || data.detail || t('admin.errorGeneric'));
      else { setImportResult(data); setImportId(''); }
    } catch { setImportError(t('settings.networkError')); }
    finally { setImportLoading(false); }
  };

  const handleProcessDemos = async () => {
    setDemosLoading(true);
    setError('');
    setDemosResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/process-demos`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok) setError(data.error || data.detail || t('admin.errorGeneric'));
      else setDemosResult(data);
    } catch { setError(t('settings.networkError')); }
    finally { setDemosLoading(false); }
  };

  const handleReconstruct = async () => {
    setRecLoading(true);
    setRecError('');
    setRecResult(null);
    try {
      const body = { force: recForce };
      if (recServerId) body.server_id = recServerId;
      const r = await fetch(`${API_URL}/api/admin/matches/${parseInt(recId)}/reconstruct`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) setRecError(data.error || data.detail || t('admin.errorGeneric'));
      else setRecResult(data);
    } catch { setRecError(t('settings.networkError')); }
    finally { setRecLoading(false); }
  };

  const handleReprocessDemo = async () => {
    setReproLoading(true);
    setReproError('');
    setReproResult(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/matches/${parseInt(reproId)}/reprocess-demo`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skip_advanced: reproSkipAdv,
          skip_positions: reproSkipPos,
          skip_highlights: reproSkipHl,
        }),
      });
      const data = await r.json();
      if (!r.ok) setReproError(data.error || data.detail || t('admin.errorGeneric'));
      else setReproResult(data);
    } catch { setReproError(t('admin.reproNetworkError')); }
    finally { setReproLoading(false); }
  };

  const filtered = matches.filter((m) => {
    if (!search) return true;
    return String(m.id).includes(search);
  });

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navMatches')}</h1>

      {error && <div className="admin-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* ID Counter */}
      {perms.hasCommand('admin.reset-counter') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchCounterTitle')}</div>
          {counterLoading ? (
            <div className="admin-info">{t('live.loading')}</div>
          ) : counter ? (
            <>
              <div className="admin-row">
                <span className="admin-label">{t('admin.matchCounterLatest')}</span>
                <span className="admin-value">{counter.pg_latest ?? '-'}</span>
              </div>
              <div className="admin-row">
                <span className="admin-label">{t('admin.matchCounterNext')}</span>
                <span className="admin-value" style={{ fontWeight: 'bold' }}>
                  {counter.pg_next ?? '-'}
                  {counter.in_sync === false && (
                    <span style={{ color: 'var(--accent-red, #f44)', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                      {t('admin.matchCounterNotSynced', { pg: counter.pg_next, maria: counter.maria_auto_increment })}
                    </span>
                  )}
                  {counter.in_sync === true && (
                    <span style={{ color: 'var(--accent-green, #4f4)', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                      {t('admin.matchCounterSynced')}
                    </span>
                  )}
                </span>
              </div>
              <div className="admin-row">
                <input
                  className="admin-input"
                  type="number"
                  placeholder={t('admin.matchCounterNextPlaceholder')}
                  value={nextId}
                  onChange={(e) => setNextId(e.target.value)}
                  style={{ maxWidth: 150 }}
                />
                <button className="admin-btn" onClick={handleSetCounter} disabled={counterSaving || !nextId}>
                  {counterSaving ? t('settings.saving') : t('admin.matchCounterSet')}
                </button>
                {counterResult && <span className="admin-success">{counterResult}</span>}
                {counterError && <span className="admin-error">{counterError}</span>}
              </div>
            </>
          ) : (
            <div className="admin-info">{t('admin.matchCounterNotImpl')}</div>
          )}
        </div>
      )}

      {/* Renumber */}
      {perms.hasCommand('admin.renumber-match') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchRenumberTitle')}</div>
          <div className="admin-card-desc">
            {t('admin.matchRenumberDesc')}
            <br />
            <strong style={{ color: 'var(--accent-red, #f44)' }}>{t('admin.matchRenumberWarnLabel')}</strong> {t('admin.matchRenumberWarnBody')}
          </div>
          <div className="admin-row">
            <input className="admin-input" type="number" placeholder={t('admin.matchRenumberOldPlaceholder')} value={renumberOld}
              onChange={(e) => setRenumberOld(e.target.value)} style={{ maxWidth: 120 }} />
            <span className="admin-label" style={{ minWidth: 'auto' }}>→</span>
            <input className="admin-input" type="number" placeholder={t('admin.matchRenumberNewPlaceholder')} value={renumberNew}
              onChange={(e) => setRenumberNew(e.target.value)} style={{ maxWidth: 120 }} />
            {!confirmRenumber ? (
              <button className="admin-btn admin-btn-danger"
                onClick={() => setConfirmRenumber(true)}
                disabled={renumberLoading || !renumberOld || !renumberNew}>
                {t('admin.matchRenumber')}
              </button>
            ) : (
              <>
                <span style={{ color: 'var(--accent-red, #f44)', fontSize: '0.85rem' }}>
                  {t('admin.matchRenumberConfirm', { old: renumberOld, new: renumberNew })}
                </span>
                <button className="admin-btn admin-btn-danger" onClick={() => { setConfirmRenumber(false); handleRenumber(); }}
                  disabled={renumberLoading}>
                  {renumberLoading ? t('admin.matchRenumbering') : t('admin.confirm')}
                </button>
                <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmRenumber(false)}>
                  {t('matchDetail.cancel')}
                </button>
              </>
            )}
          </div>
          {renumberResult && (
            <div style={{ marginTop: '0.5rem' }}>
              <div className="admin-success">{t('admin.matchRenumberDone', { old: renumberResult.old_id, new: renumberResult.new_id })}</div>
              {renumberResult.results && Object.entries(renumberResult.results).map(([sys, r]) => (
                <div key={sys} style={{ fontSize: '0.8rem', color: r.status === 'ok' ? 'var(--accent-green, #4f4)' : r.status === 'skipped' ? 'var(--text-muted)' : 'var(--accent-red, #f44)' }}>
                  {sys}: {r.status}{r.files_renamed != null ? ` (${r.files_renamed} filer)` : ''}{r.detail ? ` — ${r.detail}` : ''}
                </div>
              ))}
            </div>
          )}
          {renumberError && <div className="admin-error" style={{ marginTop: '0.5rem' }}>{renumberError}</div>}
        </div>
      )}

      {/* Delete stats */}
      {perms.hasCommand('admin.delete-stats') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchDeleteStatsTitle')}</div>
          <div className="admin-card-desc">{t('admin.matchDeleteStatsDesc')}</div>
          <div className="admin-row">
            <input className="admin-input" type="number" placeholder={t('admin.matchIdPlaceholder')} value={deleteId}
              onChange={(e) => setDeleteId(e.target.value)} style={{ maxWidth: 120 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={deleteDjango} onChange={(e) => setDeleteDjango(e.target.checked)} />
              {t('admin.matchAlsoDjango')}
            </label>
            {!confirmDelete ? (
              <button className="admin-btn admin-btn-danger"
                onClick={() => setConfirmDelete(true)}
                disabled={deleteLoading || !deleteId}>
                {t('admin.remove')}
              </button>
            ) : (
              <>
                <span style={{ color: 'var(--accent-red, #f44)', fontSize: '0.85rem' }}>
                  {t('admin.matchDeleteConfirm', { id: deleteId })}
                </span>
                <button className="admin-btn admin-btn-danger" onClick={() => { setConfirmDelete(false); handleDeleteStats(); }}
                  disabled={deleteLoading}>
                  {deleteLoading ? t('admin.removing') : t('admin.confirm')}
                </button>
                <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmDelete(false)}>
                  {t('matchDetail.cancel')}
                </button>
              </>
            )}
          </div>
          {deleteResult && (
            <div style={{ marginTop: '0.5rem' }}>
              <div className="admin-success">{t('admin.matchDeleted', { id: deleteResult.match_id })}</div>
              {deleteResult.results && Object.entries(deleteResult.results).map(([sys, r]) => (
                <div key={sys} style={{ fontSize: '0.8rem', color: r.status === 'ok' ? 'var(--accent-green, #4f4)' : 'var(--accent-red, #f44)' }}>
                  {sys}: {r.status}{r.detail ? ` — ${r.detail}` : ''}
                </div>
              ))}
            </div>
          )}
          {deleteError && <div className="admin-error" style={{ marginTop: '0.5rem' }}>{deleteError}</div>}
        </div>
      )}

      {/* Import stats */}
      {perms.hasCommand('admin.import-stats') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchImportTitle')}</div>
          <div className="admin-card-desc">{t('admin.matchImportDesc')}</div>
          <div className="admin-row">
            <input className="admin-input" type="number" placeholder={t('admin.matchIdPlaceholder')} value={importId}
              onChange={(e) => setImportId(e.target.value)} style={{ maxWidth: 120 }} />
            <button className="admin-btn" onClick={handleImportStats} disabled={importLoading || !importId}>
              {importLoading ? t('admin.matchImporting') : t('admin.matchImport')}
            </button>
          </div>
          {importResult && <div className="admin-success" style={{ marginTop: '0.5rem' }}>{t('admin.matchImported')}</div>}
          {importError && <div className="admin-error" style={{ marginTop: '0.5rem' }}>{importError}</div>}
        </div>
      )}

      {/* Process demos */}
      {perms.hasAnyCommand('match.demos') && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchDemosTitle')}</div>
          <div className="admin-card-desc">{t('admin.matchDemosDesc')}</div>
          <div className="admin-row">
            <button className="admin-btn" onClick={handleProcessDemos} disabled={demosLoading}>
              {demosLoading ? t('admin.processingElo') : t('admin.matchProcessDemos')}
            </button>
            {demosResult && <span className="admin-success">{t('admin.matchDone')}</span>}
          </div>
        </div>
      )}

      {/* Reconstruct match (rebuild teams/winner/server from MatchZy) */}
      {perms.isOwner && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchRecTitle')}</div>
          <div className="admin-card-desc">
            <strong>{t('admin.recWhatLabel')}</strong> {t('admin.recWhatBody')}
            <br /><br />
            <strong>{t('admin.recWhenLabel')}</strong>
            <ul style={{ marginTop: '0.3rem', marginBottom: 0, paddingLeft: '1.3rem' }}>
              <li>{t('admin.recWhenLi1')}</li>
              <li>{t('admin.recWhenLi2')}</li>
              <li>{t('admin.recWhenLi3')}</li>
            </ul>
            <strong>{t('admin.recFreqLabel')}</strong> {t('admin.recFreqBody')}
            <br />
            <strong>{t('admin.recForceLabel')}</strong> {t('admin.recForceBody')}
          </div>
          <div className="admin-row">
            <input className="admin-input" type="number" placeholder={t('admin.matchIdPlaceholder')} value={recId}
              onChange={(e) => setRecId(e.target.value)} style={{ maxWidth: 120 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={recForce} onChange={(e) => setRecForce(e.target.checked)} />
              {t('admin.recForceCheck')}
            </label>
            <button className="admin-btn" onClick={handleReconstruct} disabled={recLoading || !recId}>
              {recLoading ? t('admin.recReconstructing') : t('admin.recReconstruct')}
            </button>
          </div>
          <div className="admin-row" style={{ marginTop: '0.5rem' }}>
            <span className="admin-label" style={{ minWidth: 120 }}>{t('admin.recServerLabel')}</span>
            <select
              className="admin-input"
              value={recServerId}
              onChange={(e) => setRecServerId(e.target.value)}
              style={{ maxWidth: 280 }}
            >
              <option value="">{t('admin.recServerAuto')}</option>
              {serverOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.ip}{s.is_public ? ', public' : ''})
                </option>
              ))}
            </select>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {t('admin.recServerHint')}
            </span>
          </div>
          {recResult && (
            <div style={{ marginTop: '0.5rem' }}>
              <div className={recResult.status === 'reconstructed' ? 'admin-success' : 'admin-info'}>
                {t('admin.recStatus', { status: recResult.status })}
              </div>
              {recResult.team1_name && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {recResult.team1_name} ({recResult.team1_player_count}p) vs {recResult.team2_name} ({recResult.team2_player_count}p)
                  {recResult.winner_team_name && <> — {t('admin.recWinner')} <strong>{recResult.winner_team_name}</strong></>}
                  {recResult.server_name && <> — {t('admin.recServer')} {recResult.server_name}</>}
                </div>
              )}
              {recResult.unmapped_steamids?.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--accent-yellow, #fa0)' }}>
                  {t('admin.recUnmapped', { ids: recResult.unmapped_steamids.join(', ') })}
                </div>
              )}
            </div>
          )}
          {recError && <div className="admin-error" style={{ marginTop: '0.5rem' }}>{recError}</div>}
        </div>
      )}

      {/* Reprocess demo (advanced stats + positions + highlights) */}
      {perms.isOwner && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.matchReproTitle')}</div>
          <div className="admin-card-desc">
            <strong>{t('admin.recWhatLabel')}</strong> {t('admin.reproWhatBody')}
            <ul style={{ marginTop: '0.3rem', paddingLeft: '1.3rem' }}>
              <li><strong>{t('admin.reproLi1Strong')}</strong>{t('admin.reproLi1Rest')}</li>
              <li><strong>{t('admin.reproLi2Strong')}</strong>{t('admin.reproLi2Rest')}</li>
              <li><strong>{t('admin.reproLi3Strong')}</strong>{t('admin.reproLi3Rest')}</li>
            </ul>
            {t('admin.reproTiming')}
            <br /><br />
            <strong>{t('admin.recWhenLabel')}</strong>
            <ul style={{ marginTop: '0.3rem', marginBottom: 0, paddingLeft: '1.3rem' }}>
              <li>{t('admin.reproWhenLi1')}</li>
              <li>{t('admin.reproWhenLi2')}</li>
              <li>{t('admin.reproWhenLi3')}</li>
              <li>{t('admin.reproWhenLi4')}</li>
              <li>{t('admin.reproWhenLi5')}</li>
            </ul>
            <strong>{t('admin.reproReqLabel')}</strong> {t('admin.reproReqBody')}
            <br />
            <strong>{t('admin.reproSkipLabel')}</strong> {t('admin.reproSkipBody')}
            <br />
            <strong>{t('admin.reproBeforeLabel')}</strong> {t('admin.reproBeforeBody')}
          </div>
          <div className="admin-row">
            <input className="admin-input" type="number" placeholder={t('admin.matchIdPlaceholder')} value={reproId}
              onChange={(e) => setReproId(e.target.value)} style={{ maxWidth: 120 }} />
            <button className="admin-btn" onClick={handleReprocessDemo} disabled={reproLoading || !reproId}>
              {reproLoading ? t('admin.reproProcessing') : t('admin.reproReprocess')}
            </button>
          </div>
          <div className="admin-row" style={{ marginTop: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              <input type="checkbox" checked={reproSkipAdv} onChange={(e) => setReproSkipAdv(e.target.checked)} />
              {t('admin.reproSkipAdv')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              <input type="checkbox" checked={reproSkipPos} onChange={(e) => setReproSkipPos(e.target.checked)} />
              {t('admin.reproSkipPos')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              <input type="checkbox" checked={reproSkipHl} onChange={(e) => setReproSkipHl(e.target.checked)} />
              {t('admin.reproSkipHl')}
            </label>
          </div>
          {reproResult && (
            <div style={{ marginTop: '0.5rem' }}>
              <div className="admin-success">{t('admin.reproDone', { mb: reproResult.demo_size_mb })}</div>
              {reproResult.advanced_stats && (
                <div style={{ fontSize: '0.85rem', color: reproResult.advanced_stats.error ? 'var(--accent-red, #f44)' : 'var(--text-secondary)' }}>
                  Advanced: {reproResult.advanced_stats.error ? t('admin.reproFail', { error: reproResult.advanced_stats.error }) : t('admin.reproAdvCount', { updated: reproResult.advanced_stats.updated, total: reproResult.advanced_stats.total })}
                </div>
              )}
              {reproResult.positions && (
                <div style={{ fontSize: '0.85rem', color: reproResult.positions.error ? 'var(--accent-red, #f44)' : 'var(--text-secondary)' }}>
                  Positions: {reproResult.positions.error ? t('admin.reproFail', { error: reproResult.positions.error }) : t('admin.reproPosCount', { saved: reproResult.positions.saved, players: reproResult.positions.players })}
                </div>
              )}
              {reproResult.highlights && (
                <div style={{ fontSize: '0.85rem', color: reproResult.highlights.error ? 'var(--accent-red, #f44)' : 'var(--text-secondary)' }}>
                  Highlights: {reproResult.highlights.error
                    ? t('admin.reproFail', { error: reproResult.highlights.error })
                    : reproResult.highlights.success
                      ? t('admin.reproHlCount', { count: Array.isArray(reproResult.highlights.highlight_id) ? reproResult.highlights.highlight_id.length : 1, ids: JSON.stringify(reproResult.highlights.highlight_id) })
                      : t('admin.reproNoHl')}
                </div>
              )}
            </div>
          )}
          {reproError && <div className="admin-error" style={{ marginTop: '0.5rem' }}>{reproError}</div>}
        </div>
      )}

      {/* Match list */}
      <div className="admin-card">
        <div className="admin-card-title">{t('admin.matchRecentTitle')}</div>
        <div className="admin-search">
          <input placeholder={t('admin.matchSearchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {loading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('admin.colTeam1')}</th>
                <th>{t('admin.colTeam2')}</th>
                <th>{t('admin.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 20).map((m) => (
                <tr key={m.id}>
                  <td style={{ fontFamily: 'monospace' }}>{m.id}</td>
                  <td>{m.team1?.name || 'Team 1'}</td>
                  <td>{m.team2?.name || 'Team 2'}</td>
                  <td>{m.status || '-'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('admin.matchNone')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
